import { Controller } from "@hotwired/stimulus"

// Provides Mapbox address autocomplete for settings text fields.
// Attaches to a wrapper div with the input and suggestions container inside.
export default class extends Controller {
  static targets = ["input", "suggestions"]

  connect() {
    this.debounceTimer = null
    this.selectedIndex = -1
    this.mapboxToken = null
    this.loadMapboxToken()
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.inputTarget.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    this.inputTarget.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("click", this.handleClickOutside)
  }

  async loadMapboxToken() {
    try {
      const response = await fetch("/api/widget/config")
      const data = await response.json()
      this.mapboxToken = data.mapbox_token
    } catch (error) {
      console.error("Failed to load Mapbox token:", error)
    }
  }

  search() {
    const query = this.inputTarget.value.trim()
    clearTimeout(this.debounceTimer)

    if (query.length < 3) {
      this.closeSuggestions()
      return
    }

    this.debounceTimer = setTimeout(() => this.fetchSuggestions(query), 300)
  }

  async fetchSuggestions(query) {
    if (!this.mapboxToken) return

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${this.mapboxToken}&types=address&country=us&limit=4`
      const response = await fetch(url)
      const data = await response.json()

      if (data.features && data.features.length > 0) {
        this.renderSuggestions(data.features)
      } else {
        this.closeSuggestions()
      }
    } catch (error) {
      console.error("Geocoding error:", error)
    }
  }

  renderSuggestions(features) {
    this.selectedIndex = -1

    const html = features.map((feature, i) => {
      const mainText = feature.place_name.split(",")[0]
      const subText = feature.place_name.split(",").slice(1).join(",").trim()

      return `
        <div class="suggestion-item px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors ${i < features.length - 1 ? 'border-b border-gray-100' : ''}"
             data-address="${this.escapeHtml(feature.place_name)}"
             data-action="click->settings-address#selectAddress">
          <div class="text-sm font-medium text-gray-900">${this.escapeHtml(mainText)}</div>
          <div class="text-xs text-gray-500">${this.escapeHtml(subText)}</div>
        </div>
      `
    }).join("")

    this.suggestionsTarget.innerHTML = html
    this.suggestionsTarget.classList.remove("hidden")
  }

  selectAddress(event) {
    this.inputTarget.value = event.currentTarget.dataset.address
    this.closeSuggestions()
  }

  closeSuggestions() {
    this.suggestionsTarget.innerHTML = ""
    this.suggestionsTarget.classList.add("hidden")
    this.selectedIndex = -1
  }

  handleKeydown(event) {
    const items = this.suggestionsTarget.querySelectorAll(".suggestion-item")
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.highlightItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.highlightItem(items)
        break
      case "Enter":
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          event.preventDefault()
          this.inputTarget.value = items[this.selectedIndex].dataset.address
          this.closeSuggestions()
        }
        break
      case "Escape":
        this.closeSuggestions()
        break
    }
  }

  highlightItem(items) {
    items.forEach((item, index) => {
      item.classList.toggle("bg-gray-100", index === this.selectedIndex)
    })
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeSuggestions()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
